import * as angular from 'angular';
import { GoLocalizeHelperService } from 'go-modules/translation-helper/go-localize-helper.service';

/* @ngInject */
export function datetimepickerDirective (
	$compile,
	$timeout,
	$translate,
	goLocalizeHelper: GoLocalizeHelperService,
	uibDateParser
) {
	const datetimePickers = [];

	return {
		restrict: 'EA',
		require: '?ngModel',
		templateUrl: 'datetimepicker/datetimepicker.directive.html',
		scope: {
			date: '=ngModel',
			format: '@format',
			isOpen: '@isOpen',
			isReadonly: '<',
			ngChange: '&',
			minDate: '<',
			maxDate: '<'
		},
		compile (_tElement, tAttrs) {

			const tempid = tAttrs.id;

			// clear id
			tAttrs.$set('id', null);

			// set name attribute
			if (tAttrs.name) {
				tAttrs.$set('name', tAttrs.name);
			}

			return (scope, _element, attrs, ngModelCtrl: ng.INgModelController) => {
				const id = tempid;
				const selectedAriaText = $translate.instant('common_selected');
				datetimePickers.push(scope);

				// expose id to scope
				scope.id = id;

				//localize date and format, allow leading 0's in dates or not
				scope.format = goLocalizeHelper.longDateFormat.replace('MM', 'M!').replace('DD', 'd!').replaceAll('Y', 'y');

				// Expose valid date events
				let initializedWatcher = false;
				let hasDateParseError = false;
				const inputElement = _element.find('input');

				const syncDateParseStateFromInput = () => {
					const inputValue = inputElement.val();
					if (inputValue == null || inputValue === '') {
						hasDateParseError = false;
					} else {
						const parsedDate = uibDateParser.parse(inputValue, scope.format || 'shortDate');
						hasDateParseError = !angular.isDate(parsedDate);
					}

					ngModelCtrl.$setDirty();
					ngModelCtrl.$setTouched();
					ngModelCtrl.$validate();
				};

				ngModelCtrl.$validators.date = (_modelVal, viewVal) => {
					if (hasDateParseError) {
						return false;
					}

					if (viewVal == null || viewVal === '') {
						return true;
					}

					const parsedDate = uibDateParser.parse(viewVal, scope.format || 'shortDate');
					return angular.isDate(parsedDate);
				};

				scope.$watch('date', () => {
					if (!initializedWatcher) {
						// Nothing has happened yet
						initializedWatcher = true;
						return;
					}

					ngModelCtrl.$setDirty();
					ngModelCtrl.$setTouched();
					hasDateParseError = false;
					ngModelCtrl.$validate();
				});

				if (inputElement.length) {
					inputElement.on('input blur', syncDateParseStateFromInput);
					scope.$on('$destroy', () => {
						inputElement.off('input blur', syncDateParseStateFromInput);
					});
				}

				scope.$watch('minDate', (val) => {
					scope.datePickerOptions.minDate = val;
				});

				scope.$watch('maxDate', (val) => {
					scope.datePickerOptions.maxDate = val;
				});

				let timePicker = null;
				if (!attrs.hasOwnProperty('excludeTimepicker')) {

					// time picker template
					timePicker = angular.element('<datetimepicker-timepicker></datetimepicker-timepicker>');
					timePicker.attr('ng-model', 'date');
					timePicker.attr('options', 'timePickerOptions');

					scope.timePickerOptions = {
						hourStep: 1,
						minuteStep: 1,
						showMeridian: true
					};
				}

				scope.datePickerOptions = {
					startingDay: 0,
					showWeeks: false
				};

				scope.onChanged = () => {
					if (scope.ngChange) scope.ngChange({date: scope.date});
				};

				let datepickerObserver: MutationObserver | null = null;

				const getDatepickerElement = (): HTMLElement | null => {
					return _element[0].querySelector('.uib-datepicker-popup') as HTMLElement | null;
				};

				const updateButtonAriaCurrent = (datepickerElement: HTMLElement) => {
					const currentDayButton = datepickerElement.querySelector('.uib-day.current-date button');
					if (currentDayButton != null) {
						currentDayButton.setAttribute('aria-current', 'date');
					}
				};

				const updatePopupDialogAccessibility = (datepickerElement: HTMLElement) => {
					datepickerElement.setAttribute('role', 'dialog');

					if (scope.ariaLabelledby != null && scope.ariaLabelledby !== '') {
						datepickerElement.setAttribute('aria-labelledby', scope.ariaLabelledby);
						datepickerElement.removeAttribute('aria-label');
					} else {
						const inputAriaLabel = inputElement.attr('aria-label');
						if (inputAriaLabel != null && inputAriaLabel !== '') {
							datepickerElement.setAttribute('aria-label', inputAriaLabel);
						} else {
							datepickerElement.removeAttribute('aria-label');
						}
						datepickerElement.removeAttribute('aria-labelledby');
					}

					if (scope.ariaDescribedby != null && scope.ariaDescribedby !== '') {
						datepickerElement.setAttribute('aria-describedby', scope.ariaDescribedby);
					} else {
						datepickerElement.removeAttribute('aria-describedby');
					}
				};

				const updateButtonAriaLabel = (datepickerElement: HTMLElement) => {
					const selectedButton: HTMLButtonElement | null = datepickerElement.querySelector(
						'.uib-day button.btn-info'
					);

					if (selectedButton == null) {
						return;
					}

					const baseAriaLabel = selectedButton.dataset.baseAriaLabel
						?? selectedButton.getAttribute('aria-label')
						?? '';
					selectedButton.dataset.baseAriaLabel = baseAriaLabel;
					selectedButton.setAttribute('aria-label', `${baseAriaLabel}, ${selectedAriaText}`);
					selectedButton.dataset.selectedAriaLabel = 'true';
				};

				const attachDatepickerObserver = (datepickerElement: HTMLElement) => {
					if (datepickerObserver != null) {
						datepickerObserver.disconnect();
					}

					datepickerObserver = new MutationObserver(() => {
						updatePopupDialogAccessibility(datepickerElement);
						updateButtonAriaCurrent(datepickerElement);
						updateButtonAriaLabel(datepickerElement);
					});
					datepickerObserver.observe(datepickerElement, {
						childList: true,
						subtree: true
					});
				};

				const updateDatepickerAccessibility = () => {
					$timeout(() => {
						const datepickerElement = getDatepickerElement();
						if (datepickerElement == null) {
							return;
						}

						updatePopupDialogAccessibility(datepickerElement);
						updateButtonAriaCurrent(datepickerElement);
						updateButtonAriaLabel(datepickerElement);
						attachDatepickerObserver(datepickerElement);
					});
				};

				let compiledTimePicker;
				scope.$watch('isOpen', (value) => {
					scope.datePickerOptions = {
						minDate: scope.minDate,
						maxDate: scope.maxDate,
						showWeeks: false
					};
					// ensure that all datetime pickers are closed
					if (value) {
						angular.forEach(datetimePickers, (picker) => {
							if (picker.id !== id) {
								picker.isOpen = false;
							}
						});
					}

					if (!attrs.hasOwnProperty('excludeTimepicker')) {
						// add / remove timepicker from datepicker
						$timeout(() => {
							const datepickerElement = getDatepickerElement();
							if (datepickerElement == null) return;
							const datepicker = angular.element(datepickerElement);
							if (value) {
								compiledTimePicker = $compile(timePicker)(scope);

								datepicker[0].children[0].insertBefore(
									compiledTimePicker[0],
									datepicker[0].children[0].children[1]
								);

							} else if (compiledTimePicker) {
								compiledTimePicker.remove();
							}
						});
					}

					if (value) {
						updateDatepickerAccessibility();
					} else if (datepickerObserver != null) {
						datepickerObserver.disconnect();
						datepickerObserver = null;
					}
				});

				scope.$watch('date', () => {
					if (scope.isOpen) {
						updateDatepickerAccessibility();
					}
				});

				scope.ariaLabelledby = attrs.labelledBy;
				scope.ariaDescribedby = attrs.describedBy;

				attrs.$observe('disabled', (value) => {
					scope.disabled = !!value;
				});

				scope.$on('$destroy', () => {
					const index = datetimePickers.indexOf(scope);
					if (index !== -1) {
						datetimePickers.splice(index, 1);
					}

					if (datepickerObserver != null) {
						datepickerObserver.disconnect();
						datepickerObserver = null;
					}
				});
			};
		}
	};
}
